import { CampaignDto, Link } from "../services/api";

export enum AppRoutes {
  add = "add",
  apiKeys = "api-keys",
  analytics = "analytics",
  analyticsCampaignsCards = "campaigns-cards",
  analyticsCards = "cards",
  analyticsMembers = "members",
  analyticsOverview = "overview",
  analyticsPoints = "points",
  anonymous = "anonymous",
  boosts = "boosts",
  callback = "callback",
  campaigns = "campaigns",
  completeUserProfile = "complete-user-profile",
  coupons = "coupons",
  createDiscount = "create-discount",
  createProgram = "create-program",
  customize = "customize",
  customerFlows = "customer-flows",
  customers = "customers",
  customerScanned = "customer-scanned",
  customEarningFlow = "custom-earning-flow",
  design = "design",
  dialog = "dialog",
  email = "emails",
  engagement = "engagement",
  extension = "extension",
  followUs = "follow-us",
  home = "home",
  importCodes = "import-codes",
  incentivizePurchase = "incentivize-purchase-flow",
  installLoyaltyCard = "install-loyalty-card",
  integrations = "integrations",
  links = "links",
  locationBased = "location-based",
  locations = "locations",
  login = "login",
  loyalty = "loyalty",
  loyaltyHub = "loyalty-hub",
  tiers = "tiers",
  notFound = "not-found",
  oauth = "oauth",
  onboarding = "onboarding",
  plans = "plans",
  referralFlow = "referral-flow",
  rewards = "rewards",
  root = "",
  scheduled = "scheduled",
  settings = "settings",
  subscription = "subscription",
  technical = "technical",
  verifyAccount = "verify-account",
  wallets = "wallets",
  webapp = "webapp",
}

export enum AnalyticsRangePreset {
  last7Days = "last7days",
  last30Days = "last30days",
  last90Days = "last90days",
  last12Months = "last12months",
  thisYear = "thisYear",
}

export enum AppRouteParams {
  boostId = "boostId",
  campaignId = "campaignId",
  couponId = "couponId",
  customerFlowSlot = "slot",
  customerId = "customerId",
  dialogKey = "dialogKey",
  extensionType = "extensionType",
  linkId = "linkId",
  locationId = "locationId",
  apiKeyId = "apiKeyId",
  programSlug = "programSlug",
  rewardId = "rewardId",
  tierId = "tierId",
}

/** Polaris page widths a route can ask its shell for (route data `pageWidth`); without it, the default 966px column. */
export enum PageWidth {
  narrow = "narrow",
  full = "full",
}

export enum AppQueryStrings {
  chargeId = "charge_id",
  choosePlan = "choosePlan",
  dialogId = "dialogId",
  paymentRequiredReason = "paymentRequiredReason",
  rewardType = "type",
  view = "view",
}

/** Dialogs that open as a Shopify modal inside the admin (route `/:programSlug/dialog/<key>`) and as a MatDialog outside it. */
export enum AppDialogs {
  addLink = "add-link",
  addLocation = "add-location",
  addBeacon = "add-beacon",
  addNfcReader = "add-nfc-reader",
  linkPos = "link-pos",
  allowedDomain = "allowed-domain-dialog",
  generateApiKey = "generate-api-key",
  updateBalance = "update-balance",
  sendPush = "send-push",
  exportCouponCodes = "export-coupon-codes",
  cappedAmountWarning = "capped-amount-warning",
  qrWebApp = "qr-web-app",
  distribution = "distribution-dialog",
  walletPassUpdate = "wallet-pass-update",
  profileFormTarget = "profile-form-target",
  loyaltyLionApiKey = "loyalty-lion-api-key",
  // The loyalty card editor's settings (its hotspots and More wallet settings)
  walletText = "wallet-text",
  walletAdvanced = "wallet-advanced",
  walletAction = "wallet-action",
  walletColors = "wallet-colors",
  walletField = "wallet-field",
  walletFields = "wallet-fields",
  walletSection = "wallet-section",
  walletAsset = "wallet-asset",

  confirm = "confirm",
  understood = "understood",
  updateIntegrationConfirm = "update-integration-confirm",
  autoTranslateConfirm = "auto-translate-confirm",
  redemptionType = "redemption-type",
  shopifyMultiPass = "shopify-multi-pass",
  videoPlayer = "video-player",

}

export enum Languages {
  es = "es",
  en = "en",
  de = "de",
  it = "it",
  fr = "fr",
}

export enum Platforms {
  all = "*",
  ios = "ios",
  android = "android",
}

// Sentinel id of the backend-provided "all customers" segment.
export enum SegmentId {
  all = "*",
}

export enum WalletSideVisible {
  Front = "front",
  Back = "back",
}

export enum WalletPlatform {
  Apple = "ios",
  Google = "android",
}

export enum WalletAppleLayout {
  Poster = "poster",
  Classic = "classic",
}

export enum ProgramAnalyticsAggregateEnum {
  PASSES_INSTALLED = "PASSES_INSTALLED",
  CLICKS = "CLICKS",
  PUSHES_SENT = "PUSHES_SENT",
  WEBAPP_VIEWS = "WEBAPP_VIEWS",
}

export enum RegisterFieldValues {
  disabled = "disabled",
  optional = "optional",
  mandatory = "mandatory",
}

export enum UpdateCustomerBalance {
  add = "add",
  remove = "remove",
}

export enum CardType {
  notification = "notification",
  wallet = "wallet",
}

export enum BoostStatus {
  Running = "running",
  Scheduled = "scheduled",
  Past = "past",
  Archived = "archived",
}

export enum BoostRepeat {
  OneTime = "one-time",
  Recurring = "recurring",
}

// Non-earning-flow channel key shared by the points/tier multiplier maps.
export enum BoostSpecialChannel {
  Manual = "MANUAL",
}

// Campaign-wide scope key shared by multiplier maps.
export enum BoostMultiplierScope {
  All = "*",
}

// Shopify sale-origin filter for a boost. Mirrors the generated Boost.SaleChannelEnum values;
// a NULL/absent saleChannel on a boost means All. Independent of the multiplier channel maps.
export enum BoostSaleChannel {
  All = "ALL",
  OnlineStore = "ONLINE_STORE",
  Pos = "POS",
}

// How the points-boost section of the form is configured.
export enum BoostPointsMode {
  None = "none",
  All = "all",
  Channels = "channels",
}

// How the tier-boost section of the form is configured.
export enum BoostTierMode {
  None = "none",
  Fixed = "fixed",
  Channels = "channels",
}

export const CampaignType: Record<string, CampaignDto.TypeEnum> = {
  Location: CampaignDto.TypeEnum._0,
  Scheduled: CampaignDto.TypeEnum._1,
  Links: CampaignDto.TypeEnum._2,
  Triggered: CampaignDto.TypeEnum._3,
  Anonymous: CampaignDto.TypeEnum._4,
};

export const LinkVisibility: Record<string, Link.VisibilityEnum> = {
  Program: Link.VisibilityEnum._0,
  Campaign: Link.VisibilityEnum._1,
};

export enum BusinessSector {
  FoodBeverage = "food_beverage",
  Fashion = "fashion",
  Beauty = "beauty",
  Health = "health",
  Pet = "pet",
  Sports = "sports",
  Home = "home",
  Other = "other",
}

export enum BusinessInterest {
  OnlineLoyalty = "online_loyalty",
  PhysicalStores = "physical_stores",
}
